import { useState, useEffect, useRef } from "react";
import styles from "./styles/ProjectSegments.module.css";
import { useParams } from "react-router-dom";
import { useGetAllSegments } from "API/Queries/useGetAllSegments";
import ViewSegmentDetailsModal from "Components/FeatureFlip/Common/ViewUserDetailsModal/ViewUserDetailsModal";
import CommonCardFeatureSegment from "Components/FeatureFlip/Common/Cards/CommonCardFeatureSegment";
import { useFeatureFlipStore } from "Store/featureFlipStore";
import EmptyState from "Components/FeatureFlip/Common/EmptyState/EmptyState";
import { useGetAllContexts } from "API/Queries/useGetAllContexts";
import {
  searchQuery,
  SEARCH_DEBOUNCE_TIME,
  SEGMENT_PAGE_LIMIT,
} from "Utils/constants";

export default function ProjectSegments() {
  const { id } = useParams();
  const observerRef = useRef<HTMLDivElement>(null);

  const [showSegmentModal, setShowSegmentModal] = useState<boolean>(false);
  const [selectedSegmentName, setSelectedSegmentName] = useState<string>("");
  const [selectedSegmentId, setSelectedSegmentId] = useState<string>("");

  const pageSegments = useFeatureFlipStore((s) => s.pageSegments);
  const setPageSegments = useFeatureFlipStore((s) => s.setPageSegments);
  const setSegments = useFeatureFlipStore((s) => s.setSegments);
  const segments = useFeatureFlipStore((s) => s.segments);
  const searchTerm = useFeatureFlipStore((s) => s.searchTerm);
  const hasMoreDataSegments = useFeatureFlipStore((s) => s.hasMoreDataSegments);
  const setHasMoreDataSegments = useFeatureFlipStore(
    (s) => s.setHasMoreDataSegments
  );
  const setSearchTerm = useFeatureFlipStore((s) => s.setSearchTerm);
  const setContexts = useFeatureFlipStore((s) => s.setContexts);
  const limit = SEGMENT_PAGE_LIMIT;
  const offSet = (pageSegments - 1) * limit;
  const { refetch: getAllSegments } = useGetAllSegments(
    {
      query: searchQuery(id, searchTerm),
      pageNumber: pageSegments,
      pageSize: limit,
    },
    {
      onSuccess: (data) => {
        if (data.length < limit) {
          setHasMoreDataSegments(false);
        }
        if (pageSegments === 1) {
          setSegments(data);
        } else {
          setSegments([...segments, ...data]);
        }
      },
    }
  );

  const { mutate: getAllContexts } = useGetAllContexts();

  useEffect(() => {
    if (pageSegments !== 1) getAllSegments();
  }, [pageSegments]);

  // Reset when search changes
  useEffect(() => {
    if (!searchTerm) {
      setHasMoreDataSegments(true);
      setSegments([]);
    }
    setPageSegments(1);

    const debounceTimer = setTimeout(() => {
      window.scroll(0, 0);
      getAllSegments();
    }, SEARCH_DEBOUNCE_TIME);

    return () => clearTimeout(debounceTimer);
  }, [searchTerm]);
  console.log(pageSegments, hasMoreDataSegments);

  // Infinite scroll observer
  useEffect(() => {
    if (!observerRef.current || !hasMoreDataSegments) return;

    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        setPageSegments(pageSegments + 1);
      }
    });

    observer.observe(observerRef.current);
    return () => observer.disconnect();
  }, [hasMoreDataSegments, segments]);
  useEffect(() => {
    return () => {
      window.scroll(0, 0);
      setPageSegments(1);
      setHasMoreDataSegments(true);
      setSearchTerm("");
    };
  }, []);

  const handleViewSegment = (segmentId: string, name?: string) => {
    getAllContexts(String(id), {
      onSuccess: (data) => {
        setContexts(data);
        setSelectedSegmentName(name || "");
        setSelectedSegmentId(segmentId);
        setShowSegmentModal(true);
      },
    });
  };

  return (
    <div className={styles.container}>
      {segments?.length ? (
        <div className={styles.segmentList}>
          {segments?.map((segment) => (
            <CommonCardFeatureSegment
              key={segment.id}
              {...segment}
              handleClick={handleViewSegment}
              btnTitle="View Segment"
            />
          ))}
          {hasMoreDataSegments && (
            <div ref={observerRef} style={{ height: "1px" }} />
          )}
        </div>
      ) : (
        <EmptyState
          title="No Segments Found"
          text="We couldn't find any segments matching your search. Try adjusting your search terms or create a new segment."
        />
      )}

      {showSegmentModal && (
        <ViewSegmentDetailsModal
          selectedSegmentId={selectedSegmentId}
          selectedSegmentName={selectedSegmentName}
          setShowSegmentModal={setShowSegmentModal}
        />
      )}
    </div>
  );
}
